// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore lodash is in the parent dir
import _ from 'lodash';

import { QuoteCard, QuoteCardType } from 'components/QuoteCard';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { Maybe } from 'types/UtilityTypes';

import { useQuickQuoteContext } from '../context/QuickQuoteContext';
import {
  CarrierCostType,
  DATQuoteLocationType,
  DATQuoteTimeframe,
  SelectedQuoteType,
} from '../types';

interface QuoteCardsSectionProps {
  setHasUserSelectedCard: (value: boolean) => void;
  handleUpdateDATRate: (
    timeframe: DATQuoteTimeframe,
    areaType: DATQuoteLocationType,
    setUpdateRateViewError: (error: Maybe<string>) => void
  ) => Promise<void>;
  // When Kit Coach is selected, suppress provider card highlight.
  suppressProviderSelection?: boolean;
  onProviderCardSelect?: () => void;
}

export const QuoteCardsSection = ({
  setHasUserSelectedCard,
  handleUpdateDATRate,
  suppressProviderSelection = false,
  onProviderCardSelect,
}: QuoteCardsSectionProps) => {
  const {
    quoteResponse,
    quoteCards,
    selectedQuote,
    carrierCostType,
    setSelectedQuote,
    setCarrierCost,
    setCarrierCostType,
    quoteCardErrors,
    stopFee,
    setStopFee,
    intermediateStopCount,
  } = useQuickQuoteContext();

  if (!quoteResponse) return null;

  return (
    <>
      <Flex direction='col' gap='md' className='w-[95%]'>
        {quoteCards.map((card: QuoteCardType) => (
          <QuoteCard
            key={card.quoteId ?? card.type}
            carrier={card}
            isSelected={
              !suppressProviderSelection && selectedQuote === card.type
            }
            onClick={() => {
              onProviderCardSelect?.();
              setHasUserSelectedCard(true);
              setSelectedQuote(card.type);

              if (card.type === SelectedQuoteType.KENCO_FLASH_AVERAGE) {
                handleKencoAverageCardClick(
                  card,
                  carrierCostType,
                  setCarrierCost
                );
                return;
              }

              if (carrierCostType === CarrierCostType.Flat) {
                setCarrierCost(_.round(card.cost));
              } else {
                if (card?.costPerMile) {
                  setCarrierCost(card?.costPerMile);
                } else {
                  setCarrierCostType(CarrierCostType.Flat);
                  setCarrierCost(_.round(card.cost));
                }
              }
            }}
            lowConfidenceThreshold={
              quoteResponse.configuration?.lowConfidenceThreshold || 70
            }
            mediumConfidenceThreshold={
              quoteResponse.configuration?.mediumConfidenceThreshold || 80
            }
            onUpdateRateView={(timeframe, areaType, setUpdateRateViewError) =>
              handleUpdateDATRate(timeframe, areaType, setUpdateRateViewError)
            }
            stops={quoteResponse.stops || []}
            stopFee={stopFee}
            setStopFee={setStopFee}
            intermediateStopCount={intermediateStopCount}
          />
        ))}
      </Flex>

      {quoteCardErrors.length > 0 && (
        <Flex direction='col' gap='xs' className='mt-2'>
          {quoteCardErrors.map((error, i) => {
            const [source, message] = error.split(' - ');
            return (
              <Flex key={`quote-error-${i}`} gap='xs'>
                <Typography
                  key={`quote-error-${i}`}
                  variant='body-xs'
                  className='text-error-500'
                >
                  <b>{source}</b>
                  {` - ${message}`}
                </Typography>
              </Flex>
            );
          })}
        </Flex>
      )}
    </>
  );
};

const handleKencoAverageCardClick = (
  card: QuoteCardType,
  carrierCostType: CarrierCostType,
  setCarrierCost: (cost: number) => void
) => {
  if (carrierCostType === CarrierCostType.Flat) {
    setCarrierCost(_.round(card.cost));
    return;
  }

  if (carrierCostType === CarrierCostType.PerMile && card.costPerMile) {
    setCarrierCost(_.round(card.costPerMile, 2));
  }
};
